class KeyedSet {
  #map = new Map()

  #ensureKey(key) {
    if (!this.#map.has(key)) {
      this.#map.set(key, new Set())
    }
  }

  keys() {
    return this.#map.keys()
  }

  values(key) {
    if (this.#map.has(key)) {
      return this.#map.get(key).values()
    } else {
      return { [Symbol.iterator]: function* () {} }
    }
  }

  add(key, value) {
    this.#ensureKey(key)
    this.#map.get(key).add(value)
  }

  has(key, value) {
    if (!this.#map.has(key)) return false
    return this.#map.get(key).has(value)
  }

  delete(key, value) {
    if (!this.#map.has(key)) return false
    this.#map.get(key).delete(value)
    if (this.#map.get(key).size === 0) {
      this.#map.delete(key)
    }
    return true
  }
}

/**
 * Select current or future elements matching a selector
 *
 * @param {string} selector The selector to watch
 * @param {(element: Element) => void|(() => void)} callback The callback to fire for each element
 * @returns {() => void} A function to stop watching
 */
export function select(selector, callback) {
  // Find first matching element inside a root element
  let findElements = (root = document.body) => [...root.querySelectorAll(selector)]

  // Check if a given element matches the selector
  let matches = element => element.matches(selector)

  let observedRemoval = new KeyedSet()

  let results = findElements()
  for (let result of results) {
    let onRemove = callback(result)
    if (typeof onRemove === 'function') {
      observedRemoval.add(result, onRemove)
    }
  }

  let observer = new MutationObserver(mutations => {
    let reportedAdd = new Set()

    for (let mutation of mutations) {
      if (mutation.type == 'childList') {
        // Handle removed elements
        for (let removedNode of mutation.removedNodes) {
          for (let observedNode of observedRemoval.keys()) {
            if (removedNode.contains(observedNode)) {
              for (let onRemove of observedRemoval.values(observedNode)) {
                onRemove()
              }
              observedRemoval.delete(observedNode, callback)
            }
          }
        }

        // Handle added elements
        for (let addedNode of mutation.addedNodes) {
          if (addedNode.nodeType !== Node.ELEMENT_NODE) continue

          // Check the added element itself
          if (matches(addedNode)) {
            if (!reportedAdd.has(addedNode)) {
              reportedAdd.add(addedNode)
              let onRemove = callback(addedNode)
              if (typeof onRemove === 'function') {
                observedRemoval.add(addedNode, onRemove)
              }
            }
          } else {
            let results = findElements(addedNode)
            for (let result of results) {
              if (!reportedAdd.has(result)) {
                reportedAdd.add(result)
                let onRemove = callback(result)
                if (typeof onRemove === 'function') {
                  observedRemoval.add(addedNode, onRemove)
                }
              }
            }
          }
        }
      }
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
  })

  return () => {
    observer.disconnect()
  }
}
